// Low-level WebSocket client for live entity notifications. See
// docs/apis/events.md for the wire protocol and
// specs/todos/2026-07-04-02-live-entity-notifications-websocket.md for the
// design. This module is intentionally framework-agnostic (no React, no
// direct localStorage access) — front/src/contexts/LiveEventsContext.tsx
// wraps it with React state and sources the auth token from AuthContext.

export type LiveScope = { entity: string; uid?: string }

export type LiveServerFrame =
    | { type: 'hello'; protocol: number }
    | { type: 'subscribed'; entity: string; uid?: string }
    | { type: 'unsubscribed'; entity: string; uid?: string }
    | { type: 'update'; entity: string; uid?: string; tables?: string[] }
    // The resident channel's only content frame (spec 2026-09-08-03): a
    // reply-token connection is told the WHOLE view — the same projection
    // GET /c/{org}/{token}/state returns — rather than a hint, because it has
    // no scoped API to re-fetch through.
    | { type: 'view'; entity: string; view: unknown }
    | { type: 'resync' }
    | { type: 'error'; code: string; title: string }

export interface LiveSocketCallbacks {
    /** Fires once per successful connection, after the "hello" frame arrives. */
    onOpen: () => void
    /** Fires for every update/resync/subscribed/unsubscribed/error frame. */
    onFrame: (frame: LiveServerFrame) => void
    /**
     * Fires when the connection drops. `permanent` is true for outcomes the
     * caller should NOT retry (4403 forbidden, or an HTTP 404 on the upgrade
     * attempt meaning the feature is disabled / no access) — the handle stops
     * reconnecting itself in that case and the caller should treat the
     * socket as permanently unavailable for this session.
     */
    onClose: (info: { code: number; reason: string; permanent: boolean }) => void
}

export interface LiveSocketOptions {
    /**
     * A conversation reply token, for the resident's page. When set, the
     * in-band auth frame carries `reply_token` instead of `token` and the
     * connection authorizes a capability over exactly ONE conversation — see
     * internal/handlers/events/resident.go. `getToken` is then never called:
     * a resident has no session, and sending a stale one would be both
     * useless and a leak.
     */
    replyToken?: string
}

export interface LiveSocketHandle {
    /** Send a subscribe/unsubscribe frame. No-ops silently if not connected —
     * the caller (LiveEventsContext) is responsible for replaying active
     * subscriptions once onOpen fires again after a reconnect. */
    send: (frame: Record<string, unknown>) => void
    /** Tears down the socket and stops all future reconnect attempts. */
    close: () => void
}

// Close codes from docs/apis/events.md's table.
const CLOSE_AUTH_FAILED = 4401
const CLOSE_FORBIDDEN = 4403
const CLOSE_SERVICE_RESTART = 1012

const BACKOFF_BASE_MS = 1000
const BACKOFF_CAP_MS = 30000
const BACKOFF_JITTER = 0.3 // +/- 30%

function backoffDelay(attempt: number): number {
    const raw = Math.min(BACKOFF_CAP_MS, BACKOFF_BASE_MS * 2 ** attempt)
    const jitter = raw * BACKOFF_JITTER * (Math.random() * 2 - 1)
    return Math.max(0, Math.round(raw + jitter))
}

function wsPathFor(org: string): string {
    return `/api/v1/organizations/${org}/events/ws`
}

function wsUrlFor(org: string): string {
    const scheme = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
    return `${scheme}//${window.location.host}${wsPathFor(org)}`
}

// probeAvailability issues a plain (non-upgrade) GET at the events endpoint
// to distinguish "feature disabled" (HTTP 404, per the handler's
// realtime.enabled check — see internal/handlers/events/handler.go) from a
// genuine network failure. The browser WebSocket API gives no way to read
// the HTTP status of a failed upgrade directly, so this one extra plain-GET
// round trip on the FIRST connection attempt only is the only reliable
// signal available. The handler recognizes a non-upgrade request and
// answers it directly with a clean `200 OK` + `{"websocket":true}` JSON
// body (never reaching the WS library's upgrade path), so a healthy probe
// never shows up as an error in DevTools/HARs. Any outcome other than a
// clean 404 is treated as "assume available" — connect() will find out the
// real story soon enough (including a 503 over capacity, which is
// retryable, unlike 404). `signal` lets close() abort an in-flight probe
// (e.g. React StrictMode's dev-only double-mount tearing down the first
// handle before its probe resolves).
async function probeAvailability(org: string, signal: AbortSignal): Promise<'available' | 'disabled'> {
    try {
        const res = await fetch(wsPathFor(org), { method: 'GET', signal })
        return res.status === 404 ? 'disabled' : 'available'
    } catch {
        return 'available'
    }
}

/**
 * connectLiveSocket opens (and transparently reconnects) a WebSocket to the
 * live-notifications endpoint for `org`. `getToken` is called fresh on every
 * connection attempt — including reconnects — so a token refreshed by
 * AuthContext in the meantime is picked up without the caller needing to
 * tear down and recreate the handle.
 *
 * Reconnect policy: jittered exponential backoff (1s base, 30s cap, ±30%
 * jitter) on any close EXCEPT 4403 (forbidden — permanent) and an HTTP 404
 * on the very first upgrade attempt (feature disabled / no access —
 * permanent). 4401 (auth failed/expired), 1006 (abnormal closure), and 1012
 * (server restart) all reconnect — the next attempt's fresh getToken() call
 * is what actually fixes a 4401 if the caller refreshed the token in
 * response to onClose.
 */
export function connectLiveSocket(
    org: string,
    getToken: () => string | null,
    callbacks: LiveSocketCallbacks,
    options?: LiveSocketOptions,
): LiveSocketHandle {
    let ws: WebSocket | null = null
    let stopped = false
    let reconnectAttempt = 0
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null
    let probed = false
    // Aborts the in-flight availability probe fetch on close() — otherwise a
    // handle torn down while its probe is still pending (e.g. React
    // StrictMode's dev-only double-mount, where the first LiveEventsContext
    // effect instance is cleaned up almost immediately) leaves that fetch
    // running to completion for no purpose.
    const probeAbort = new AbortController()
    // helloReceived gates send(): the server's read loop only starts AFTER
    // its handshake completes (see internal/handlers/events/handler.go's
    // serveConn), so a frame sent before "hello" would sit unread in the OS
    // receive buffer rather than being lost — harmless, but sending it only
    // once we know the server is actually ready to read it is cleaner and
    // avoids relying on that buffering behavior.
    let helloReceived = false

    function clearReconnectTimer() {
        if (reconnectTimer !== null) {
            clearTimeout(reconnectTimer)
            reconnectTimer = null
        }
    }

    function scheduleReconnect() {
        if (stopped) return
        clearReconnectTimer()
        const delay = backoffDelay(reconnectAttempt)
        reconnectAttempt += 1
        reconnectTimer = setTimeout(connect, delay)
    }

    function connect() {
        if (stopped) return

        // Only probe once, before the first-ever connection attempt — a
        // disabled feature won't become enabled mid-session, so there is no
        // value in re-probing on every reconnect (and doing so would add a
        // round trip to every retry).
        if (!probed) {
            probed = true
            probeAvailability(org, probeAbort.signal).then((result) => {
                if (stopped) return
                if (result === 'disabled') {
                    stopped = true
                    callbacks.onClose({ code: 404, reason: 'realtime feature disabled', permanent: true })
                    return
                }
                connectSocket()
            })
            return
        }

        connectSocket()
    }

    function connectSocket() {
        if (stopped) return
        helloReceived = false

        let socket: WebSocket
        try {
            socket = new WebSocket(wsUrlFor(org))
        } catch {
            // Constructing a WebSocket can throw synchronously for a malformed
            // URL — treat exactly like an immediate abnormal close so the
            // caller's onClose/reconnect logic runs uniformly.
            callbacks.onClose({ code: 1006, reason: 'failed to construct WebSocket', permanent: false })
            scheduleReconnect()
            return
        }
        ws = socket

        socket.onopen = () => {
            // Send the auth frame immediately — see docs/apis/events.md's
            // handshake step 2. The browser WebSocket API cannot set an
            // Authorization header on the upgrade request, so every browser
            // connection authenticates in-band. getToken() is called here
            // (not captured earlier) so a token refreshed since the LAST
            // reconnect attempt is what actually gets sent.
            // Exactly one credential — the server refuses a frame carrying
            // both, so the two modes can never be conflated.
            if (options?.replyToken) {
                socket.send(JSON.stringify({ type: 'auth', reply_token: options.replyToken }))
            } else {
                const token = getToken()
                if (token) {
                    socket.send(JSON.stringify({ type: 'auth', token }))
                }
            }
            // onOpen (the public callback) fires only once "hello" arrives —
            // see the message handler below — not here on the raw WS open.
        }

        socket.onmessage = (event) => {
            let frame: LiveServerFrame
            try {
                frame = JSON.parse(event.data as string) as LiveServerFrame
            } catch {
                return // malformed frame — ignore, the connection stays open
            }

            if (frame.type === 'hello') {
                helloReceived = true
                reconnectAttempt = 0 // a clean handshake resets the backoff ladder
                callbacks.onOpen()
                return
            }

            callbacks.onFrame(frame)
        }

        socket.onerror = () => {
            // WebSocket's error event carries no useful detail (by spec) — the
            // close event that follows is what actually drives reconnect
            // logic, so this handler intentionally does nothing beyond what
            // the browser already does (firing a close event next).
        }

        socket.onclose = (event) => {
            ws = null
            const permanent = event.code === CLOSE_FORBIDDEN
            callbacks.onClose({ code: event.code, reason: event.reason, permanent })
            if (permanent) {
                stopped = true
                return
            }
            scheduleReconnect()
        }
    }

    connect()

    return {
        send(frame) {
            if (ws && ws.readyState === WebSocket.OPEN && helloReceived) {
                ws.send(JSON.stringify(frame))
            }
        },
        close() {
            stopped = true
            clearReconnectTimer()
            probeAbort.abort()
            if (ws) {
                ws.onclose = null // avoid triggering a reconnect from our own deliberate close
                ws.close(1000, 'client closing')
                ws = null
            }
        },
    }
}

// Re-exported for callers that need to distinguish these without importing
// the numeric literals directly (e.g. tests).
export const LiveCloseCodes = {
    AUTH_FAILED: CLOSE_AUTH_FAILED,
    FORBIDDEN: CLOSE_FORBIDDEN,
    SERVICE_RESTART: CLOSE_SERVICE_RESTART,
} as const
